import { useEffect, useState, useMemo, useCallback } from "react";
import "./EventUserMapping.css";
import { Button, Select, Table, Input, message, Progress, Modal } from "antd";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import Lottie from "lottie-react";
import loaderJson from "../../LottieAnimation/wave-loader.json";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { BaseUrl } from "../../Constants/Constants";
import { useDebounce } from "use-debounce";

const { Option } = Select;
const { Search } = Input;

const userColumn = [
  {
    title: "Department",
    dataIndex: "department",
    key: "department",
    align: "center",
  },
  {
    title: "Manager Name",
    dataIndex: "username",
    key: "username",
    align: "center",
  },
  { title: "Email", dataIndex: "email", key: "email", align: "center" },
];

const nonLinkedColumn = [...userColumn];

const EventUserMapping = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // ✅ Get event IDs from location state OR sessionStorage
  const getEventIds = () => {
    const stateIds = location.state?.eventIds;
    if (stateIds && stateIds.length > 0) {
      // Store in sessionStorage for future refreshes
      sessionStorage.setItem("selectedEventIds", JSON.stringify(stateIds));
      return stateIds;
    }
    
    // Fallback to sessionStorage if state is lost (on refresh)
    const storedIds = sessionStorage.getItem("selectedEventIds");
    return storedIds ? JSON.parse(storedIds) : [];
  };

  const [finalEventIds, setFinalEventIds] = useState(getEventIds());
  const [userData, setUserData] = useState([]);
  const [nonLinkedData, setNonLinkedData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [linkedRowKeys, setLinkedRowKeys] = useState([]);
  const [searchLinked, setSearchLinked] = useState("");
  const [searchNonLinked, setSearchNonLinked] = useState("");
  const [filters, setFilters] = useState({
    level: "",
    domain: "",
    department: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [assignProgress, setAssignProgress] = useState(0);

  const [debouncedSearchLinked] = useDebounce(searchLinked, 200);
  const [debouncedSearchNonLinked] = useDebounce(searchNonLinked, 200);

  // ✅ Update finalEventIds if location state changes (optional safeguard)
  useEffect(() => {
    const ids = getEventIds();
    if (ids.length > 0 && JSON.stringify(ids) !== JSON.stringify(finalEventIds)) {
      setFinalEventIds(ids);
    }
  }, [location.state]);

  // Reusable function to fetch teams data
  const fetchTeamsData = useCallback(async () => {
    if (!finalEventIds || finalEventIds.length === 0) {
      console.warn("No event IDs to fetch teams for");
      message.warning("No events selected. Please go back and select events.");
      return;
    }

    try {
      const idsString = finalEventIds.join(",");
      console.log("Fetching teams for event IDs:", finalEventIds);

      const [unmappedRes, mappedRes] = await Promise.all([
        axios.get(
          `${BaseUrl}event-user-mapping/unassigned-teams-for-events?eventIds=${idsString}`,
          { withCredentials: true }
        ),
        axios.get(
          `${BaseUrl}event-user-mapping/assigned-teams-for-events?eventIds=${idsString}`,
          { withCredentials: true }
        ),
      ]);

      // Update unmapped teams --> Show only active teams
      const unmappedTeams = (unmappedRes.data?.data?.teams || []).filter(
        (team) => team.isActive === true
      );
      setNonLinkedData(
        unmappedTeams.map((team) => ({
          key: team.team_id,
          department: team.Team_name,
          username: team.manager_username,
          email: team.manager_mail,
        }))
      );

      // Update mapped teams --> Show only active teams
      const mappedTeams = (mappedRes.data?.data?.teams || []).filter(
        (team) => team.isActive === true
      );
      setUserData(
        mappedTeams.map((team) => ({
          key: team.team_id,
          department: team.Team_name,
          username: team.manager_username,
          email: team.manager_mail,
        }))
      );

      return { success: true };
    } catch (err) {
      console.error("Error fetching teams:", err);
      message.error("Failed to load teams");
      return { success: false, error: err };
    }
  }, [finalEventIds]);

  // ✅ Getting the Mapped and UnMapped Teams
  useEffect(() => {
    if (!finalEventIds || finalEventIds.length === 0) {
      console.warn("No event IDs available for fetching teams");
      return;
    }

    const initialFetch = async () => {
      setIsLoading(true);
      await fetchTeamsData();
      setIsLoading(false);
    };

    initialFetch();
  }, [finalEventIds, fetchTeamsData]);

  // ✅ Clean up sessionStorage when component unmounts
  useEffect(() => {
    return () => {
      sessionStorage.removeItem("selectedEventIds");
      console.log("SessionStorage cleared on unmount");
    };
  }, []);

  const rowSelection = { selectedRowKeys, onChange: setSelectedRowKeys };
  const linkedRowSelection = {
    selectedRowKeys: linkedRowKeys,
    onChange: setLinkedRowKeys,
  };

  const applyFilters = useCallback(
    (data = []) => {
      if (!Array.isArray(data)) return [];
      return data.filter(
        (user) => !filters.department || user.department === filters.department
      );
    },
    [filters.department]
  );

  // ✅ Filter for Mapped Teams
  const filteredUserData = useMemo(() => {
    if (!Array.isArray(userData)) return [];

    const filtered = applyFilters(userData);

    if (!debouncedSearchLinked) return filtered;

    const search = debouncedSearchLinked.toLowerCase();
    return filtered.filter((u) => {
      return (
        (u.username || "").toLowerCase().includes(search) ||
        (u.email || "").toLowerCase().includes(search) ||
        (u.department || "").toLowerCase().includes(search)
      );
    });
  }, [userData, debouncedSearchLinked, applyFilters]);

  // ✅ Filter for UnMapped Teams
  const filteredNonLinkedData = useMemo(() => {
    if (!Array.isArray(nonLinkedData)) return [];

    const filtered = applyFilters(nonLinkedData);

    if (!debouncedSearchNonLinked) return filtered;

    const search = debouncedSearchNonLinked.toLowerCase();
    return filtered.filter((u) => {
      return (
        (u.username || "").toLowerCase().includes(search) ||
        (u.email || "").toLowerCase().includes(search) ||
        (u.department || "").toLowerCase().includes(search)
      );
    });
  }, [nonLinkedData, debouncedSearchNonLinked, applyFilters]);

  // ✅ Assigning Events To Teams with Progress
  const handleAssign = async () => {
    console.log("Selected Team Keys:", selectedRowKeys);
    console.log("Event IDs:", finalEventIds);

    if (selectedRowKeys.length === 0) {
      return message.warning("Please select teams to assign!");
    }

    if (!finalEventIds || finalEventIds.length === 0) {
      return message.error("No events to assign!");
    }

    const currentDate = new Date().toISOString().split("T")[0];

    try {
      setIsAssigning(true);
      setAssignProgress(0);

      // Simulate progress
      const progressInterval = setInterval(() => {
        setAssignProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const res = await axios.post(
        `${BaseUrl}event-user-mapping/assign-events-to-teams`,
        {
          eventIds: finalEventIds,
          teamIds: selectedRowKeys,
          start_date: currentDate,
          end_date: currentDate,
        },
        { withCredentials: true }
      );

      clearInterval(progressInterval);
      setAssignProgress(100);

      if (res?.data?.status === "Ok" && res?.data?.statuscode === 200) {
        message.success(res.data.message || "Events assigned successfully!");
        setSelectedRowKeys([]);

        // Reuse fetchTeamsData function
        await fetchTeamsData();

        // ✅ Clear sessionStorage after successful assignment
        sessionStorage.removeItem("selectedEventIds");
        console.log("SessionStorage cleared after assignment");
        
      } else {
        message.error(res?.data?.error?.message || "Assignment failed!");
      }
    } catch (err) {
      console.error("Assignment error:", err);
      message.error(
        err?.response?.data?.message ||
          "Assignment failed! Please try again."
      );
    } finally {
      setTimeout(() => {
        setIsAssigning(false);
        setAssignProgress(0);
      }, 500);
    }
  };

  //✅ Removing Events for Mapped Teams
  const handleRemove = async () => {
    if (linkedRowKeys.length === 0) {
      return message.warning("Please select teams to remove!");
    }

    if (!finalEventIds || finalEventIds.length === 0) {
      return message.error("No events to unassign from!");
    }

    try {
      setIsLoading(true);

      const payload = {
        eventIds: finalEventIds,
        teamIds: linkedRowKeys,
      };

      console.log("Unassign Payload:", payload);

      const res = await axios.post(
        `${BaseUrl}event-user-mapping/unassign-teams-to-events`,
        payload,
        { withCredentials: true }
      );

      if (res?.data?.status === "Ok" && res?.data?.statuscode === 200) {
        message.success(res.data.message || "Teams unassigned successfully!");
        setLinkedRowKeys([]);

        //  Reuse fetchTeamsData function
        await fetchTeamsData();
      } else {
        message.error(
          res?.data?.error?.message || "Failed to unassign teams!"
        );
      }
    } catch (err) {
      console.error("Unassign error:", err);
      message.error(
        err?.response?.data?.message ||
          "Failed to unassign teams! Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Handle back navigation with storage cleanup
  const handleBackNavigation = () => {
    sessionStorage.removeItem("selectedEventIds");
    console.log("SessionStorage cleared on back navigation");
    navigate("/create-events");
  };

  return (
    <div className="userMappingMain">
      {/* Normal Loading for general operations */}
      {isLoading && !isAssigning && (
        <div className="loader-overlay">
          <Lottie animationData={loaderJson} loop style={{ height: 200 }} />
        </div>
      )}

      {/* Custom Circular Progress for Assign Operation */}
      <Modal
        open={isAssigning}
        footer={null}
        closable={false}
        centered
        width={400}
        styles={{
          body: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px 20px",
          },
        }}
      >
        <Progress
          type="circle"
          percent={assignProgress}
          strokeColor={{
            "0%": "#108ee9",
            "100%": "#87d068",
          }}
          size={150}
        />
        <h3 style={{ marginTop: 20, fontSize: 18, fontWeight: 500 }}>
          Assigning Events to Teams
        </h3>
        <p style={{ color: "#888", marginTop: 10, textAlign: "center" }}>
          Please wait until completion...
        </p>
      </Modal>

      <p className="back">
        <span
          onClick={handleBackNavigation}
          style={{
            color: "#4096FF",
            textDecoration: "underline",
            cursor: "pointer",
          }}
        >
          /Events
        </span>
        <ArrowForwardIosIcon className="arrow" />
      </p>

      <h2>Assign Events</h2>

      <div className="top-bar">
        <div className="filter-row">
          <Select
            placeholder="Select Department"
            style={{ marginRight: 5, width: 200 }}
            value={filters.department || undefined}
            allowClear
            onChange={(value) =>
              setFilters((p) => ({ ...p, department: value || "" }))
            }
          >
            <Option value="Development">Development</Option>
            <Option value="Testing">Testing</Option>
            <Option value="Devops">Devops</Option>
          </Select>
        </div>

        <div className="buttons-inline">
          <Button
            onClick={handleAssign}
            className="addCourseButton"
            disabled={selectedRowKeys.length === 0}
          >
            Assign Teams ({selectedRowKeys.length})
          </Button>
          <Button
            onClick={handleRemove}
            className="addCourseButton"
            disabled={linkedRowKeys.length === 0}
          >
            Remove ({linkedRowKeys.length})
          </Button>
        </div>
      </div>

      <div className="Tableset">
        <div>
          <h2>Mapped Teams</h2>
          <Search
            placeholder="Search by name, email, or department"
            value={searchLinked}
            onChange={(e) => setSearchLinked(e.target.value)}
            style={{ marginBottom: 16, width: 300 }}
            allowClear
          />
          <Table
            rowSelection={linkedRowSelection}
            columns={userColumn}
            dataSource={filteredUserData}
            pagination={{ pageSize: 10 }}
          />
        </div>

        <div>
          <h2>Unmapped Teams</h2>
          <Search
            placeholder="Search by name, email, or department"
            value={searchNonLinked}
            onChange={(e) => setSearchNonLinked(e.target.value)}
            style={{ marginBottom: 16, width: 300 }}
            allowClear
          />
          <Table
            rowSelection={rowSelection}
            columns={nonLinkedColumn}
            dataSource={filteredNonLinkedData}
            pagination={{ pageSize: 10 }}
          />
        </div>
      </div>
    </div>
  );
};

export default EventUserMapping;